import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import axios from "@/features/axiosHelper";

//Default filter
function computeDefaultWindow() {
  const start = dayjs().subtract(7, "day").startOf("day");
  const end = dayjs().add(1, "day").startOf("day").subtract(1, "minute");
  return { start, end };
}

export const loadDateFilter = createAsyncThunk(
  "dateFilter/load",
  async ({ area, preferenceKey }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const current = state.sessionInfo?.dateFilter?.[area]; //area homepage or integrations

      //Check if dateFilter is already loaded
      if (current?.startDate && current?.endDate) {
        return {
          area,
          startDate: current.startDate,
          endDate: current.endDate,
          source: current.source || "session",
          alreadyHydrated: true,
        };
      }
      //fetch user pref
      let pref;
      try {
        const result = await axios.get("/account/user-preferences", {
          params: { key: preferenceKey },
        });
        pref = result?.data;
      } catch {
        //will fallback
      }

      // Normalize preference payload
        let parsed = null;
        if (typeof pref === "string") {
            try {
                parsed = JSON.parse(pref);
            } catch {
                parsed = null; 
            }
        } else if (typeof pref === "object") {
            parsed = pref;
        }

      let startDate = null;
      let endDate = null;
      let source = "default";

      //Valid response check
      if (
        parsed &&
        parsed.startDateISO &&
        parsed.expiresAtISO &&
        dayjs(parsed.expiresAtISO).isAfter(dayjs())
      ) {
        const candidate = dayjs(parsed.startDateISO);
        if (candidate.isValid()) {
          startDate = candidate;
          endDate = dayjs().add(1, "day").startOf("day").subtract(1, "minute");
          source = "remote";
        }
      }

      //use default if invalid
      if (!startDate || !endDate) {
        const { start, end } = computeDefaultWindow();
        startDate = start;
        endDate = end;
        source = "default";
      }

      return {
        area,
        startDate,
        endDate,
        source,
        alreadyHydrated: false,
      };
    } catch (e) {
      const { start, end } = computeDefaultWindow();
      return rejectWithValue({
        area,
        startDate: start,
        endDate: end,
        source: "default",
        error: e.message,
      });
    }
  }
);

const dateFilterSlice = createSlice({
  name: "dateFilter",
  initialState: {
    home: {
      startDate: null,
      endDate: null,
      ready: false,
      source: null, // 'user', 'session', 'default'
    },
    integration: {
      startDate: null,
      endDate: null,
      ready: false,
      source: null,
    },
    messages: {
      startDate: null,
      endDate: null,
      ready: false,
      source: null,
    },
  },
  reducers: {
    setHomeDateFilter: (state, action) => {
      const { startDate, endDate, source } = action.payload;
      state.home.startDate = startDate;
      state.home.endDate = endDate;
      if (source) {
        state.home.source = source;
      }
      if (startDate && endDate) {
        state.home.ready = true;
      }
    },
    setIntegrationDateFilter: (state, action) => {
      const { startDate, endDate, source } = action.payload;
      state.integration.startDate = startDate;
      state.integration.endDate = endDate;
      if (source) {
        state.integration.source = source;
      }
      if (startDate && endDate) {
        state.integration.ready = true;
      }
    },
    setMessagesDateFilter: (state, action) => {
      const { startDate, endDate, source } = action.payload;
      state.messages.startDate = startDate;
      state.messages.endDate = endDate;
      if (source) {
        state.messages.source = source;
      }
      if (startDate && endDate) {
        state.messages.ready = true;
      }
    },
    setHomeDateFilterReady: (state, action) => {
      state.home.ready = action.payload?.ready ?? true;
    },
    setIntegrationDateFilterReady: (state, action) => {
      state.integration.ready = action.payload?.ready ?? true;
    },
    setMessagesDateFilterReady: (state, action) => {
      state.messages.ready = action.payload?.ready ?? true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDateFilter.fulfilled, (state, action) => {
        const { area, startDate, endDate, source, alreadyHydrated } =
          action.payload;
        if (!alreadyHydrated) {
          state[area].startDate = startDate;
          state[area].endDate = endDate;
          state[area].source = source;
        }
        state[area].ready = true;
      })
      .addCase(loadDateFilter.rejected, (state, action) => {
        // Rejection still contains fallback payload
        const { area, startDate, endDate, source } = action.payload || {};
        if (area) {
          state[area].startDate = startDate;
          state[area].endDate = endDate;
          state[area].source = source || "default";
          state[area].ready = true;
        }
      });
  },
});

export const {
  setHomeDateFilter,
  setIntegrationDateFilter,
  setMessagesDateFilter,
  setHomeDateFilterReady,
  setIntegrationDateFilterReady,
  setMessagesDateFilterReady,
} = dateFilterSlice.actions;

// Selectors
export const selectHomeDateFilter = (state) =>
  state.sessionInfo.dateFilter.home;
export const selectIntegrationDateFilter = (state) =>
  state.sessionInfo.dateFilter.integration;
export const selectMessagesDateFilter = (state) =>
  state.sessionInfo.dateFilter.messages;

export default dateFilterSlice.reducer;
